import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { NavigationEnd, Router } from "@angular/router";
import { Subject, forkJoin, fromEvent, merge, of } from "rxjs";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
} from "rxjs/operators";

import { DataService } from "../../providers/data/data.service";
import { GET_CATALOG_BRANDS } from "../brands-page/brands-page.graphql";
import { slugifyBrandName } from "../brands-page/brands-page.constants";
import { SEARCH_SUGGESTIONS } from "./product-search-bar.graphql";
import {
    HighlightPart,
    SearchSuggestionBrand,
    SearchSuggestionProduct,
    SearchSuggestionsResult,
} from "./product-search-bar.types";

const RECENT_SEARCHES_KEY = "venduro.recentSearches";
const MAX_RECENT = 8;
const MAX_BRANDS = 5;
const MAX_PRODUCTS = 5;

@Component({
    selector: "vsf-product-search-bar",
    templateUrl: "./product-search-bar.component.html",
    styleUrls: ["./product-search-bar.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductSearchBarComponent implements OnInit, OnDestroy {
    @Input() placeholder: string | undefined;
    @ViewChild("searchField", { static: true })
    searchField?: ElementRef<HTMLElement>;

    searchTerm = new UntypedFormControl("");
    open = false;
    loading = false;
    recentSearches: string[] = [];
    brands: SearchSuggestionBrand[] = [];
    products: SearchSuggestionProduct[] = [];
    activeIndex = -1;
    panelStyle: Record<string, string> = {};

    private readonly destroy$ = new Subject<void>();
    private panelId = `search-suggest-${Math.random().toString(36).slice(2, 9)}`;

    constructor(
        private router: Router,
        private dataService: DataService,
        private host: ElementRef<HTMLElement>,
        private cdr: ChangeDetectorRef,
    ) {}

    get panelDomId(): string {
        return this.panelId;
    }

    get term(): string {
        return String(this.searchTerm.value || "").trim();
    }

    get filteredRecent(): string[] {
        const term = this.term.toLowerCase();
        if (!term) {
            return this.recentSearches.slice(0, 5);
        }
        return this.recentSearches
            .filter((item) => item.toLowerCase().includes(term))
            .slice(0, 5);
    }

    get hasSuggestions(): boolean {
        return (
            this.filteredRecent.length > 0 ||
            this.brands.length > 0 ||
            this.products.length > 0
        );
    }

    get showPanel(): boolean {
        return this.open && (this.loading || this.hasSuggestions || this.term.length > 0);
    }

    get flatOptions(): Array<
        | { type: "recent"; value: string }
        | { type: "brand"; value: SearchSuggestionBrand }
        | { type: "product"; value: SearchSuggestionProduct }
    > {
        return [
            ...this.filteredRecent.map((value) => ({ type: "recent" as const, value })),
            ...this.brands.map((value) => ({ type: "brand" as const, value })),
            ...this.products.map((value) => ({ type: "product" as const, value })),
        ];
    }

    ngOnInit(): void {
        this.recentSearches = this.readRecent();
        this.syncTermFromUrl();

        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.syncTermFromUrl();
                this.closePanel();
            });

        merge(
            fromEvent(window, "resize", { passive: true }),
            fromEvent(window, "scroll", { passive: true, capture: true }),
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.open) {
                    this.updatePanelPosition();
                }
            });

        this.searchTerm.valueChanges
            .pipe(
                map((value) => String(value || "").trim()),
                distinctUntilChanged(),
                tap((term) => {
                    this.activeIndex = -1;
                    if (!term) {
                        this.brands = [];
                        this.products = [];
                        this.loading = false;
                        this.cdr.markForCheck();
                    }
                }),
                debounceTime(300),
                switchMap((term) => {
                    if (!term || term.length < 2) {
                        this.brands = [];
                        this.products = [];
                        this.loading = false;
                        this.cdr.markForCheck();
                        return of(null);
                    }
                    this.loading = true;
                    this.cdr.markForCheck();
                    return this.fetchSuggestions(term).pipe(
                        catchError(() => of(null)),
                    );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((payload) => {
                if (!payload) {
                    this.loading = false;
                    this.cdr.markForCheck();
                    return;
                }
                this.products = payload.products;
                this.brands = payload.brands;
                this.loading = false;
                this.updatePanelPosition();
                this.cdr.markForCheck();
            });
    }

    private fetchSuggestions(term: string) {
        const products$ = this.dataService
            .query<SearchSuggestionsResult>(
                SEARCH_SUGGESTIONS,
                {
                    input: {
                        term,
                        take: MAX_PRODUCTS,
                        groupByProduct: true,
                    },
                },
                "cache-first",
            )
            .pipe(
                take(1),
                catchError(() => of(null)),
            );

        const brands$ = this.dataService
            .query<{
                catalogBrands: {
                    items: Array<{ id: string; name: string }>;
                };
            }>(
                GET_CATALOG_BRANDS,
                {
                    take: MAX_BRANDS,
                    skip: 0,
                    filterTerm: term,
                },
                "cache-first",
            )
            .pipe(
                take(1),
                catchError(() => of(null)),
            );

        return forkJoin({ products: products$, brands: brands$ }).pipe(
            map(({ products, brands }) => ({
                products: (products?.search?.items || []).slice(0, MAX_PRODUCTS),
                brands: (brands?.catalogBrands?.items || []).map((item) => ({
                    id: String(item.id),
                    name: item.name,
                    count: 0,
                })),
            })),
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener("document:click", ["$event"])
    onDocumentClick(event: MouseEvent): void {
        if (!this.host.nativeElement.contains(event.target as Node)) {
            this.closePanel();
        }
    }

    onFocus(): void {
        this.open = true;
        this.updatePanelPosition();
        this.cdr.markForCheck();
    }

    onClear(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.searchTerm.setValue("");
        this.brands = [];
        this.products = [];
        this.activeIndex = -1;
        this.open = true;
        this.cdr.markForCheck();
    }

    onKeydown(event: KeyboardEvent): void {
        const options = this.flatOptions;
        if (event.key === "Escape") {
            this.closePanel();
            return;
        }
        if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!this.open) {
                this.open = true;
            }
            this.activeIndex =
                options.length === 0
                    ? -1
                    : (this.activeIndex + 1) % options.length;
            this.cdr.markForCheck();
            return;
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!options.length) {
                return;
            }
            this.activeIndex =
                this.activeIndex <= 0
                    ? options.length - 1
                    : this.activeIndex - 1;
            this.cdr.markForCheck();
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            if (this.activeIndex >= 0 && options[this.activeIndex]) {
                this.activateOption(options[this.activeIndex]);
                return;
            }
            this.submitSearch(this.term);
        }
    }

    selectRecent(term: string, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.submitSearch(term);
    }

    selectBrand(brand: SearchSuggestionBrand, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.rememberRecent(brand.name);
        this.closePanel();
        this.router.navigate(["/brands", slugifyBrandName(brand.name)]);
    }

    selectProduct(product: SearchSuggestionProduct, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.rememberRecent(product.productName);
        this.closePanel();
        const supplierId = product.cheapestVariant?.supplierId;
        if (supplierId != null && String(supplierId).length > 0) {
            this.router.navigate(["/product", product.slug, supplierId]);
            return;
        }
        this.router.navigate(["/product", product.slug]);
    }

    submitSearch(rawTerm: string): void {
        const term = String(rawTerm || "").trim();
        if (!term) {
            return;
        }
        this.rememberRecent(term);
        this.searchTerm.setValue(term, { emitEvent: false });
        this.closePanel();
        this.router.navigate(["/search"], {
            queryParams: { search: term },
            queryParamsHandling: "merge",
        });
    }

    highlight(text: string): HighlightPart[] {
        const source = String(text || "");
        const term = this.term;
        if (!source || !term) {
            return [{ text: source, match: false }];
        }
        const lower = source.toLowerCase();
        const needle = term.toLowerCase();
        const index = lower.indexOf(needle);
        if (index < 0) {
            return [{ text: source, match: false }];
        }
        return [
            { text: source.slice(0, index), match: false },
            { text: source.slice(index, index + needle.length), match: true },
            { text: source.slice(index + needle.length), match: false },
        ].filter((part) => part.text.length > 0);
    }

    productMeta(product: SearchSuggestionProduct): string {
        const brand = product.customProductMappings?.brand?.toString().trim();
        const gtin = product.customProductMappings?.gtin?.toString().trim();
        if (brand && gtin) {
            return `${brand} · GTIN ${gtin}`;
        }
        if (brand) {
            return brand;
        }
        if (gtin) {
            return `GTIN ${gtin}`;
        }
        return "";
    }

    hasImage(product: SearchSuggestionProduct): boolean {
        const preview = product.productAsset?.preview;
        return !!preview && !String(preview).includes("placeholder");
    }

    trackBrand(_: number, brand: SearchSuggestionBrand): string {
        return brand.id;
    }

    trackProduct(_: number, product: SearchSuggestionProduct): string {
        return product.productId;
    }

    private activateOption(
        option:
            | { type: "recent"; value: string }
            | { type: "brand"; value: SearchSuggestionBrand }
            | { type: "product"; value: SearchSuggestionProduct },
    ): void {
        if (option.type === "recent") {
            this.selectRecent(option.value);
            return;
        }
        if (option.type === "brand") {
            this.selectBrand(option.value);
            return;
        }
        this.selectProduct(option.value);
    }

    private closePanel(): void {
        this.open = false;
        this.activeIndex = -1;
        this.cdr.markForCheck();
    }

    private updatePanelPosition(): void {
        const field =
            this.searchField?.nativeElement ||
            this.host.nativeElement.querySelector(".search-bar__field");
        if (!field) {
            return;
        }
        const rect = field.getBoundingClientRect();
        const gutter = 8;
        const viewportPad = 12;
        const width = Math.max(rect.width, 280);
        let left = rect.left;
        if (left + width > window.innerWidth - viewportPad) {
            left = Math.max(viewportPad, window.innerWidth - width - viewportPad);
        }
        this.panelStyle = {
            position: "fixed",
            top: `${Math.round(rect.bottom + gutter)}px`,
            left: `${Math.round(left)}px`,
            width: `${Math.round(width)}px`,
            zIndex: "1000",
        };
    }

    private syncTermFromUrl(): void {
        const tree = this.router.parseUrl(this.router.url);
        const search = String(tree.queryParams?.["search"] || "");
        if (search !== String(this.searchTerm.value || "")) {
            this.searchTerm.setValue(search, { emitEvent: false });
            this.cdr.markForCheck();
        }
    }

    private rememberRecent(term: string): void {
        const value = String(term || "").trim();
        if (!value) {
            return;
        }
        const next = [
            value,
            ...this.recentSearches.filter(
                (item) => item.toLowerCase() !== value.toLowerCase(),
            ),
        ].slice(0, MAX_RECENT);
        this.recentSearches = next;
        try {
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
        } catch {
        }
    }

    private readRecent(): string[] {
        try {
            const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
            if (!raw) {
                return [];
            }
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) {
                return [];
            }
            return parsed
                .map((item) => String(item || "").trim())
                .filter(Boolean)
                .slice(0, MAX_RECENT);
        } catch {
            return [];
        }
    }
}
